import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'reactstrap';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Field, FieldArray, getFormValues, reduxForm } from 'redux-form';
import { Link, Prompt, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import HEADER_ICON from 'mdi-react/FormatTextIcon';
import RICH_TEXT_ICON from 'mdi-react/FormatTextboxIcon';
import IMAGE_ICON from 'mdi-react/ImageOutlineIcon';
import IMAGE_PHOTOGRAPHER_ICON from 'mdi-react/CameraIcon';
import DELIMITER_ICON from 'mdi-react/FormatPageBreakIcon';
import YOUTUBE_VIDEO_ICON from 'mdi-react/YoutubeIcon';
import CUSTOM_VIDEO_ICON from 'mdi-react/PlayCircleOutlineIcon';
import CARD_OUTLINE_ICON from 'mdi-react/CardIcon';
import I_FRAME_ICON from 'mdi-react/IframeIcon';
import IMAGE_CAROUSEL_ICON from 'mdi-react/ImageMultipleOutlineIcon';
import FACT_BOX_ICON from 'mdi-react/InformationOutlineIcon';
import COUNT_UP_ICON from 'mdi-react/CounterIcon';
import COUNT_DOWN_ICON from 'mdi-react/TimerSandIcon';
import DragIcon from 'mdi-react/DragIcon';
import ArrowUpIcon from 'mdi-react/ArrowUpIcon';
import ArrowDownIcon from 'mdi-react/ArrowDownIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import CheckIcon from 'mdi-react/CheckIcon';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import CodeTagsIcon from 'mdi-react/CodeTagsIcon';
import HistoryIcon from 'mdi-react/HistoryIcon';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';
import CreationIcon from 'mdi-react/CreationIcon';
import ViewAgendaIcon from 'mdi-react/ViewAgendaIcon';
import t, { partial } from '../../../util/translation/translation';
import Api from '../../../util/api';
import TextEditor from '../../../shared/components/text-editor/TextEditor';
import TextEditor2 from '../../../shared/components/text-editor/TextEditor2';
import renderDropZoneField from '../../../shared/components/form/DropZoneMS';
import EmptyStateInfo from '../../../shared/components/custom/EmptyStateInfo';
import FormModal from '../../../shared/components/custom/FormModal';
import renderDropZoneVideoField from '../../../shared/components/form/DropZoneVideo';
import renderTexField from '../../../shared/components/custom/Field';
import renderTextAreaField from '../../../shared/components/custom/TextArea';
import SquareIconButton from '../../../shared/components/custom/SquareIconButton';
import SwitchRow from '../../../shared/components/custom/SwitchRow';
import ChoiceCards from '../../../shared/components/custom/ChoiceCards';
import AppPreviewModal from './AppPreviewModal';
import EmbedPreview from './EmbedPreview';
import AiDraftModal from './AiDraftModal';
import AiTextEditorModal from './AiTextEditorModal';
import AiVersionsModal from './AiVersionsModal';
import CarouselSlidesField from './CarouselSlidesField';
import {
  AVAILABLE_COMPONENTS,
  aiPageInfo,
  blockSummary,
  isAiPage,
  isBlockEmpty,
  isYoutubeId,
  parseCount,
  stateFromComponents,
  youtubeId,
} from './newsUtils';

const s = partial('shared');
const p = partial('NewsForm');
const getIconComponent = (type) => {
  switch (type) {
    case 'RICH_TEXT':
      return RICH_TEXT_ICON;
    case 'RICH_TEXT2':
      return RICH_TEXT_ICON;
    case 'IMAGE':
      return IMAGE_ICON;
    case 'IMAGE_CAROUSEL':
      return IMAGE_CAROUSEL_ICON;
    case 'FACT_BOX':
      return FACT_BOX_ICON;
    case 'IMAGE_PHOTOGRAPHER':
      return IMAGE_PHOTOGRAPHER_ICON;
    case 'HEADER':
      return HEADER_ICON;
    case 'DELIMITER':
      return DELIMITER_ICON;
    case 'YOUTUBE_VIDEO':
      return YOUTUBE_VIDEO_ICON;
    case 'SPORTALITY_VIDEO':
      return CUSTOM_VIDEO_ICON;
    case 'CUSTOM_VIDEO':
      return CUSTOM_VIDEO_ICON;
    // Video, before its source is chosen
    case 'VIDEO':
      return CUSTOM_VIDEO_ICON;
    case 'I_FRAME':
      return I_FRAME_ICON;
    // An iframe block holding a page written with AI
    case 'AI_PAGE':
      return CreationIcon;
    case 'BUTTON':
      return CARD_OUTLINE_ICON;
    case 'COUNT_UP':
      return COUNT_UP_ICON;
    case 'COUNT_DOWN':
      return COUNT_DOWN_ICON;
    default:
      return null;
  }
};

// The video blocks, one per source: added as one Video block, which asks
// where the video is from and then becomes that block
const VIDEO_SOURCES = [
  'CUSTOM_VIDEO',
  'YOUTUBE_VIDEO',
  'SPORTALITY_VIDEO',
].filter((type) => AVAILABLE_COMPONENTS.some(({ id }) => id === type));
const VIDEO_BLOCK = { name: 'video', id: 'VIDEO' };
// A block's definition, the Video one included
const blockDefinition = (type) =>
  type === 'VIDEO'
    ? VIDEO_BLOCK
    : AVAILABLE_COMPONENTS.find(({ id }) => id === type);

// The blocks that can be added, grouped as in the add menu
// Text becomes a fact box with a switch, and an image a carousel when a
// second image is added, so those two aren't offered on their own
const BLOCK_GROUPS = [
  ['text', ['RICH_TEXT2']],
  ['media', ['IMAGE', 'VIDEO']],
  ['other', ['BUTTON', 'COUNT_UP', 'COUNT_DOWN', 'I_FRAME', 'DELIMITER']],
].map(([group, types]) => [
  group,
  types.filter((type) =>
    type === 'VIDEO' ? VIDEO_SOURCES.length > 0 : !!blockDefinition(type)
  ),
]);

// The palette, in the add menu's order: the old Text block stays only in
// articles that already have it. With AI pages, writing one comes first
const paletteItems = (aiPage) => [
  ...(aiPage ? [{ name: 'aiPage', id: 'AI_PAGE' }] : []),
  ...BLOCK_GROUPS.flatMap(([, types]) => types).map(blockDefinition),
];

// With AI pages: written by AI, and added as an iframe block
const BLOCK_GROUPS_WITH_AI = BLOCK_GROUPS.map(([group, types]) => [
  group,
  group === 'other' ? ['AI_PAGE', ...types] : types,
]);

// Picks a block to add: a full button, or (`inline`) the small + between
// blocks. `aiPage` offers a page written with AI too.
const AddBlockMenu = ({ onAdd, inline, aiPage }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (!ref.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);
  return (
    <div
      ref={ref}
      className={`news-add${inline ? ' news-add--inline' : ''}${
        open ? ' news-add--open' : ''
      }`}
    >
      {inline ? (
        <button
          type="button"
          className="news-add__plus"
          aria-label={p('addBlockHere')}
          title={p('addBlockHere')}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <PlusIcon size={16} />
        </button>
      ) : (
        <button
          type="button"
          className="news-add__button"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <PlusIcon size={18} />
          {p('addBlock')}
        </button>
      )}
      {open && (
        <div className="news-add__menu">
          {(aiPage ? BLOCK_GROUPS_WITH_AI : BLOCK_GROUPS).map(
            ([group, types]) => (
              <div className="news-add__group" key={group}>
                <span className="news-add__group-title">
                  {p(`blockGroup-${group}`)}
                </span>
                <div className="news-add__grid">
                  {types.map((type) => {
                    const Icon = getIconComponent(type);
                    return (
                      <button
                        type="button"
                        key={type}
                        className="news-add__item"
                        onClick={() => {
                          setOpen(false);
                          onAdd(type);
                        }}
                      >
                        <span className="news-add__item-icon">
                          <Icon size={18} />
                        </span>
                        {p(`${type}_title`)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

// A field in a block, with its label above it
const Labelled = ({ label, hint, children }) => (
  <div className="form__form-group news-block__field">
    <span className="form-header">{label}</span>
    {children}
    {hint && <small className="news-block__hint">{hint}</small>}
  </div>
);

// One block of the article: a header (drag grip, type, a summary when
// folded, actions) and its fields. Only the grip drags, so text in the
// fields can be selected.
const NewsFieldWrapper = ({
  children,
  id,
  type,
  dragHandleProps,
  onRemove,
  onUp,
  onDown,
  onSelect,
  onToggle,
  isFirst,
  isLast,
  active,
  empty,
  collapsed,
  summary,
}) => {
  const IconComponent = getIconComponent(type);
  const canFold = type !== 'DELIMITER';
  const summaryText =
    summary.count !== undefined
      ? p('imageCount', { count: summary.count })
      : summary.text;
  return (
    // Clicking or typing in a block marks it in the app preview
    <div
      className={`news-block${active ? ' news-block--active' : ''}${
        empty ? ' news-block--empty' : ''
      }${collapsed || !canFold ? ' news-block--folded' : ''}`}
      data-block-id={id}
      onMouseDown={onSelect}
      onFocus={onSelect}
    >
      <div className="news-block__head">
        <span
          className="news-block__grip"
          title={p('dragToMove')}
          {...dragHandleProps}
          aria-label={p('dragToMove')}
        >
          <DragIcon size={22} />
        </span>
        <span className="news-block__icon">
          <IconComponent size={18} />
        </span>
        {canFold ? (
          <button
            type="button"
            className="news-block__title"
            aria-expanded={!collapsed}
            onClick={onToggle}
          >
            <strong>{p(`${type}_title`)}</strong>
            {collapsed && summary.image && (
              <img className="news-block__thumb" src={summary.image} alt="" />
            )}
            {collapsed && summaryText && (
              <span className="news-block__summary">{summaryText}</span>
            )}
          </button>
        ) : (
          <span className="news-block__title">
            <strong>{p(`${type}_title`)}</strong>
          </span>
        )}
        {empty && <span className="news-block__empty">{p('emptyBlock')}</span>}
        <div className="icon-actions news-block__actions">
          {canFold && (
            <SquareIconButton
              icon={
                <ChevronDownIcon
                  className={`news-block__chevron${
                    collapsed ? '' : ' news-block__chevron--open'
                  }`}
                />
              }
              title={collapsed ? p('expandBlock') : p('collapseBlock')}
              id={`fold-${id}`}
              onClick={onToggle}
            />
          )}
          <SquareIconButton
            icon={<ArrowUpIcon />}
            title={p('IMAGE_CAROUSEL_moveUp')}
            id={`up-${id}`}
            disabled={isFirst}
            onClick={onUp}
          />
          <SquareIconButton
            icon={<ArrowDownIcon />}
            title={p('IMAGE_CAROUSEL_moveDown')}
            id={`down-${id}`}
            disabled={isLast}
            onClick={onDown}
          />
          <SquareIconButton
            icon={<DeleteOutlineIcon />}
            title={s('delete')}
            tone="danger"
            id={`delete-${id}`}
            onClick={onRemove}
          />
        </div>
      </div>
      {/* Hidden rather than removed when folded: the editors would tidy
          their text again on coming back, which reads as a change */}
      {canFold && (
        <div className="news-block__body" hidden={collapsed}>
          {children}
        </div>
      )}
    </div>
  );
};

// A block in the palette, dragged into the article to add it
const ContentBuilderCard = ({ item, isDragging }) => {
  const IconComponent = getIconComponent(item.id);
  return (
    <div
      className={`news-palette-card${
        item.id === 'AI_PAGE' ? ' news-palette-card--ai' : ''
      }${isDragging ? ' news-palette-card--dragging' : ''}`}
    >
      <span className="news-block__icon">
        <IconComponent size={18} />
      </span>
      <span className="news-palette-card__label">
        {item.id === 'AI_PAGE' ? p('aiDraftButton') : p(`${item.id}_title`)}
      </span>
    </div>
  );
};

const NewsComponentsForm = ({
  onSubmitPublish,
  onSubmitSave,
  change,
  initialize,
  components,
  formValues,
  news,
  newsObjects,
  activeBlock,
  onSelectBlock,
}) => {
  const { pathname } = useLocation();
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [columns, setColumns] = useState({
    Col0: {
      name: 'Available',
      value: 'Available',
      // The old Text block stays only in articles that already have it
      items: AVAILABLE_COMPONENTS.filter(({ id }) => id !== 'RICH_TEXT'),
    },
    Col1: {
      value: 'Current',
      name: 'Current',
      items: [],
    },
  });
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  // After a publish was stopped: empty blocks are marked until filled in
  const [showEmpty, setShowEmpty] = useState(false);
  const [emptyModalOpen, setEmptyModalOpen] = useState(false);
  const [scrollTo, setScrollTo] = useState(null);
  // Blocks folded to their header (not saved; just for working on the page)
  const [folded, setFolded] = useState([]);
  // Writing a page with AI: the club's use this month (null when the club
  // doesn't have it, which hides the button) and whether the modal is open
  const [aiUsage, setAiUsage] = useState(null);
  // The palette: writing a page with AI first, for clubs that can
  const palette = paletteItems(!!aiUsage);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  // Where the page being written goes (null: at the end)
  const [aiInsertAt, setAiInsertAt] = useState(null);
  // Changing an AI page: its block (kept after closing, so a change being
  // written is followed again on reopening) and whether the modal is open
  const [aiEditBlock, setAiEditBlock] = useState(null);
  const [aiEditOpen, setAiEditOpen] = useState(false);
  // An AI page whose words are being changed by hand
  const [textEditBlock, setTextEditBlock] = useState(null);
  // AI page blocks showing their HTML (hidden by default)
  const [shownHtml, setShownHtml] = useState([]);
  // The AI page block whose HTML was just copied (its button shows a tick)
  const [copiedHtml, setCopiedHtml] = useState(null);
  // The AI page block whose versions are shown
  const [versionsBlock, setVersionsBlock] = useState(null);
  // The editors tidy their values right after loading; changes in that
  // moment aren't the user's
  const settleUntil = useRef(0);
  const previousValues = useRef(null);
  const nodeRef = useRef();
  const items = columns.Col1.items;
  const values = formValues || {};

  useEffect(() => {
    const { state, items: loadedItems } = stateFromComponents(components);
    settleUntil.current = Date.now() + 1000;
    initialize(state);
    setColumns((prev) => ({
      ...prev,
      Col1: {
        ...prev.Col1,
        items: loadedItems,
      },
    }));
    setDirty(false);
    setShowEmpty(false);
    // initialize is a stable redux-form dispatch; omitted to avoid
    // re-running on every render.
    // eslint-disable-next-line
  }, [components]);

  // A changed field makes the content unsaved (the block order is followed
  // where it changes, so selectedColums is left out here)
  useEffect(() => {
    const previous = previousValues.current;
    previousValues.current = formValues;
    if (!previous || !formValues || Date.now() < settleUntil.current) return;
    const keys = new Set([
      ...Object.keys(previous),
      ...Object.keys(formValues),
    ]);
    keys.delete('selectedColums');
    if ([...keys].some((key) => previous[key] !== formValues[key])) {
      setDirty(true);
    }
  }, [formValues]);

  // Leaving with unsaved changes (closing the tab; in-app links use Prompt)
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const loadAiUsage = () =>
    Api.news
      .aiDraftUsage()
      .then((usage) => setAiUsage(usage?.enabled ? usage : null))
      .catch(() => setAiUsage(null));
  useEffect(() => {
    loadAiUsage();
  }, []);

  // A block that was just added, or an empty one to fill in, is brought
  // into view
  useEffect(() => {
    if (!scrollTo) return;
    document
      .querySelector(`[data-block-id="${scrollTo}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setScrollTo(null);
  }, [scrollTo]);

  const setItems = (nextItems) => {
    setColumns((prev) => ({
      ...prev,
      Col1: {
        ...prev.Col1,
        items: nextItems,
      },
    }));
    setDirty(true);
  };

  // Col0 is the palette of blocks, Col1 the article: dragging from the
  // palette adds a block, dragging a block back onto it removes the block
  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (source.droppableId === 'Col0') {
      // Writing a page with AI opens the writer for that spot
      if (destination.droppableId === 'Col1') {
        addAt(destination.index)(palette[source.index].id);
      }
      return;
    }
    if (destination.droppableId === 'Col0') {
      setItems(items.filter((_, index) => index !== source.index));
      return;
    }
    const reordered = [...items];
    const [moved] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, moved);
    setItems(reordered);
  };

  // A unique id per added block, so it never picks up the values of a
  // removed block with the same id
  const addBlocks = (types, index) => {
    const stamp = Date.now();
    const added = types.map((type, i) => ({
      ...blockDefinition(type),
      id: `${type}-${stamp}${i}`,
    }));
    setItems([...items.slice(0, index), ...added, ...items.slice(index)]);
    onSelectBlock(added[0].id);
    setScrollTo(added[0].id);
  };

  const openAiWriter = (index) => {
    setAiInsertAt(index);
    setAiModalOpen(true);
  };
  // The add menus: an AI page opens the writer, the rest are added at once
  const addAt = (index) => (type) =>
    type === 'AI_PAGE' ? openAiWriter(index) : addBlocks([type], index);

  // A page written with AI goes in as a full-width iframe block where it
  // was asked for (at the end from the button)
  const insertAiPage = (html) => {
    const id = `I_FRAME-${Date.now()}0`;
    const index = aiInsertAt === null ? items.length : aiInsertAt;
    setItems([
      ...items.slice(0, index),
      { ...AVAILABLE_COMPONENTS.find((c) => c.id === 'I_FRAME'), id },
      ...items.slice(index),
    ]);
    change(id, html);
    change(`${id}_fullWidth`, true);
    setAiModalOpen(false);
    onSelectBlock(id);
    setScrollTo(id);
  };
  // A changed page replaces the block's; undo puts the old one back
  const replaceAiPage = (html) => {
    setAiEditOpen(false);
    replaceBlockHtml(aiEditBlock, html, p('aiEditDone'));
  };
  // The page with its words changed; undo puts the old one back
  const saveAiText = (html) => {
    const id = textEditBlock;
    setTextEditBlock(null);
    replaceBlockHtml(id, html, p('aiTextEditDone'));
  };
  // An earlier version back in the block
  const restoreAiVersion = (html) => {
    const id = versionsBlock;
    setVersionsBlock(null);
    replaceBlockHtml(id, html, p('aiVersionUsed'));
  };
  // A block's HTML replaced (a change or a version): undo puts it back
  const replaceBlockHtml = (id, html, message) => {
    const before = values[id];
    change(id, html);
    const toastId = toast.info(
      <span className="news-undo">
        {message}
        <button
          type="button"
          className="news-undo__button"
          onClick={() => {
            change(id, before);
            toast.dismiss(toastId);
          }}
        >
          {p('undo')}
        </button>
      </span>,
      { autoClose: 8000 }
    );
  };
  const copyHtml = (blockId) => async () => {
    try {
      await navigator.clipboard.writeText(values[blockId] || '');
      setCopiedHtml(blockId);
      setTimeout(
        () =>
          setCopiedHtml((current) => (current === blockId ? null : current)),
        2000
      );
      toast.success(p('aiPageCopied'));
    } catch {
      toast.error(p('aiPageCopyFailed'));
    }
  };
  const toggleHtml = (blockId) => () =>
    setShownHtml((prev) =>
      prev.includes(blockId)
        ? prev.filter((id) => id !== blockId)
        : [...prev, blockId]
    );
  // A block turned into another kind in its place. It keeps the end of its
  // id, so turning it back finds the values it had (e.g. a fact box's
  // heading)
  const replaceBlock = (blockId, type) => {
    const id = `${type}-${blockId.slice(blockId.indexOf('-') + 1)}`;
    setItems(
      items.map((item) =>
        item.id === blockId ? { ...blockDefinition(type), id } : item
      )
    );
    setFolded((prev) => prev.map((item) => (item === blockId ? id : item)));
    onSelectBlock(id);
    return id;
  };
  // Text shown as a fact box (with a heading), or back as plain text
  const setFactBox = (blockId, on) => {
    const id = replaceBlock(blockId, on ? 'FACT_BOX' : 'RICH_TEXT2');
    change(id, values[blockId]);
  };
  // A second image makes the image a carousel, the image as its first slide
  // (a carousel left with one image is saved as an image again)
  const addSecondImage = (blockId) => {
    const id = replaceBlock(blockId, 'IMAGE_CAROUSEL');
    change(`${id}_slides`, [
      {
        image: values[`${blockId}_image`],
        link: values[`${blockId}_link`] || '',
        author: values[`${blockId}_author`] || '',
      },
      { author: '', link: '' },
    ]);
    change(`${id}_fullWidth`, !!values[`${blockId}_fullWidth`]);
  };

  // A Video block becomes the block for the source picked, in its place
  const chooseVideoSource = (blockId) => (type) => {
    const id = `${type}-${Date.now()}0`;
    setItems(
      items.map((item) =>
        item.id === blockId ? { ...blockDefinition(type), id } : item
      )
    );
    onSelectBlock(id);
  };
  const videoSources = VIDEO_SOURCES.map((type) => ({
    value: type,
    icon: getIconComponent(type),
    label: p(`videoSource_${type}`),
    hint: p(`videoSource_${type}_hint`),
  }));

  // Its values stay in the form, so undo brings the block back as it was
  const handleRemoveItem = (itemId) => () => {
    const index = items.findIndex((item) => item.id === itemId);
    const removed = items[index];
    setItems(items.filter((item) => item.id !== itemId));
    const toastId = toast.info(
      <span className="news-undo">
        {p('blockRemoved', { block: p(`${itemId.split('-')[0]}_title`) })}
        <button
          type="button"
          className="news-undo__button"
          onClick={() => {
            setColumns((prev) => {
              const restored = [...prev.Col1.items];
              restored.splice(index, 0, removed);
              return { ...prev, Col1: { ...prev.Col1, items: restored } };
            });
            setDirty(true);
            toast.dismiss(toastId);
          }}
        >
          {p('undo')}
        </button>
      </span>,
      { autoClose: 8000 }
    );
  };
  const toggleFolded = (itemId) => () =>
    setFolded((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  const handleMoveItem = (itemId, goingUp) => () => {
    const moveIndex = goingUp ? -1 : 1;
    const foundIdx = items.findIndex((item) => item.id === itemId);
    const _items = [...items];
    const value = _items[foundIdx];
    _items.splice(foundIdx, 1); // remove the element at the old position
    _items.splice(foundIdx + moveIndex, 0, value); // insert it at the new position
    setItems(_items);
  };

  const save = async (publish) => {
    setSaving(true);
    const ok = await (publish ? onSubmitPublish : onSubmitSave)(formValues);
    setSaving(false);
    if (ok) {
      setDirty(false);
      setSaved(true);
      setShowEmpty(false);
    }
  };

  const emptyBlocks = items
    .map((item, index) => ({ ...item, index }))
    .filter((item) => isBlockEmpty(item.id, values));

  const handlePublish = () => {
    if (emptyBlocks.length) {
      setEmptyModalOpen(true);
      return;
    }
    save(true);
  };

  const fillInEmptyBlocks = () => {
    setEmptyModalOpen(false);
    setShowEmpty(true);
    onSelectBlock(emptyBlocks[0].id);
    setScrollTo(emptyBlocks[0].id);
  };

  const renderField = (fieldId, fieldIndex, arrayLength, dragHandleProps) => {
    const fieldBase = fieldId.split('-')[0];
    // Saved as an iframe; shown as what it is
    const aiPage = fieldBase === 'I_FRAME' && isAiPage(values[fieldId]);
    const additionalData = {
      id: fieldId,
      isFirst: fieldIndex === 0,
      isLast: fieldIndex === arrayLength - 1,
      type: aiPage ? 'AI_PAGE' : fieldBase,
      active: activeBlock === fieldId,
      empty: showEmpty && isBlockEmpty(fieldId, values),
      onSelect: () => onSelectBlock(fieldId),
      dragHandleProps,
      collapsed: folded.includes(fieldId),
      onToggle: toggleFolded(fieldId),
      summary: blockSummary(fieldId, values),
      onRemove: handleRemoveItem(fieldId),
      onUp: handleMoveItem(fieldId, true),
      onDown: handleMoveItem(fieldId, false),
    };
    switch (fieldBase) {
      case 'IMAGE':
        return (
          <NewsFieldWrapper {...additionalData}>
            <Field
              saveBase64Cropped
              name={`${fieldId}_image`}
              maxSize={3}
              component={renderDropZoneField}
              crop="NEWS_OPTIONS_CROP"
              displayCrop
            />
            <div className="news-block__row">
              <Labelled label={s('link')}>
                <Field
                  name={`${fieldId}_link`}
                  component={renderTexField}
                  placeholder="https://"
                />
              </Labelled>
              <Labelled label={p('IMAGE_PHOTOGRAPHER_title')}>
                <Field
                  name={`${fieldId}_author`}
                  component={renderTexField}
                  placeholder={p('IMAGE_PHOTOGRAPHER_title')}
                />
              </Labelled>
            </div>
            <Button
              type="button"
              size="sm"
              color="primary"
              className="news-block__add-image"
              onClick={() => addSecondImage(fieldId)}
            >
              <PlusIcon size={16} /> {p('addSecondImage')}
            </Button>
            {/* The app shows it edge to edge, without the side margins */}
            <Field
              name={`${fieldId}_fullWidth`}
              component={SwitchRow}
              label={p('I_FRAME_fullWidth')}
            />
          </NewsFieldWrapper>
        );
      case 'IMAGE_CAROUSEL':
        return (
          <NewsFieldWrapper {...additionalData}>
            <FieldArray
              name={`${fieldId}_slides`}
              component={CarouselSlidesField}
            />
            {/* The app shows it edge to edge, without the side margins */}
            <Field
              name={`${fieldId}_fullWidth`}
              component={SwitchRow}
              label={p('I_FRAME_fullWidth')}
            />
          </NewsFieldWrapper>
        );
      case 'BUTTON':
        return (
          <NewsFieldWrapper {...additionalData}>
            <div className="news-block__row">
              <Labelled label={p('BUTTON_text')}>
                <Field
                  name={`${fieldId}_text`}
                  component={renderTexField}
                  placeholder={p('BUTTON_text')}
                />
              </Labelled>
              <Labelled label={s('link')}>
                <Field
                  name={`${fieldId}_link`}
                  component={renderTexField}
                  placeholder="https://"
                />
              </Labelled>
            </div>
          </NewsFieldWrapper>
        );
      case 'COUNT_UP':
        return (
          <NewsFieldWrapper {...additionalData}>
            <div className="news-block__row">
              <Labelled label={p('COUNT_UP_prefix')}>
                <Field
                  name={`${fieldId}_prefix`}
                  component={renderTexField}
                  placeholder={p('COUNT_UP_prefixPlaceholder')}
                />
              </Labelled>
              <Labelled
                label={p('COUNT_UP_value')}
                hint={
                  values[fieldId] && parseCount(values[fieldId]) === null
                    ? p('COUNT_UP_valueInvalid')
                    : null
                }
              >
                <Field
                  name={fieldId}
                  component={renderTexField}
                  inputMode="decimal"
                  placeholder="12 500"
                />
              </Labelled>
              <Labelled label={p('COUNT_UP_suffix')}>
                <Field
                  name={`${fieldId}_suffix`}
                  component={renderTexField}
                  placeholder={p('COUNT_UP_suffixPlaceholder')}
                />
              </Labelled>
            </div>
            <Labelled label={p('COUNT_UP_label')}>
              <Field
                name={`${fieldId}_label`}
                component={renderTexField}
                placeholder={p('COUNT_UP_labelPlaceholder')}
              />
            </Labelled>
          </NewsFieldWrapper>
        );
      case 'COUNT_DOWN':
        return (
          <NewsFieldWrapper {...additionalData}>
            <div className="news-block__row">
              <Labelled
                label={p('COUNT_DOWN_date')}
                hint={
                  values[fieldId] && new Date(values[fieldId]) <= new Date()
                    ? p('COUNT_DOWN_datePassed')
                    : null
                }
              >
                <Field
                  name={fieldId}
                  component={renderTexField}
                  type="datetime-local"
                />
              </Labelled>
              <Labelled label={p('COUNT_DOWN_label')}>
                <Field
                  name={`${fieldId}_label`}
                  component={renderTexField}
                  placeholder={p('COUNT_DOWN_labelPlaceholder')}
                />
              </Labelled>
            </div>
            <Labelled
              label={p('COUNT_DOWN_endText')}
              hint={p('COUNT_DOWN_endTextHint')}
            >
              <Field
                name={`${fieldId}_endText`}
                component={renderTexField}
                placeholder={p('COUNT_DOWN_endTextPlaceholder')}
              />
            </Labelled>
          </NewsFieldWrapper>
        );
      case 'RICH_TEXT':
        return (
          <NewsFieldWrapper {...additionalData}>
            <Field
              name={fieldId}
              component={(prop) => (
                <TextEditor
                  editorState={prop.input.value}
                  setEditorState={prop.input.onChange}
                />
              )}
            />
          </NewsFieldWrapper>
        );
      // Text, or text in a fact box (with a heading): one block, switched
      case 'RICH_TEXT2':
      case 'FACT_BOX': {
        const isFactBox = fieldBase === 'FACT_BOX';
        return (
          <NewsFieldWrapper {...additionalData}>
            {isFactBox && (
              <Labelled label={p('FACT_BOX_heading')}>
                <Field
                  name={`${fieldId}_title`}
                  component={renderTexField}
                  placeholder={p('FACT_BOX_heading')}
                />
              </Labelled>
            )}
            <Field name={fieldId} component={TextEditor2} />
            <SwitchRow
              input={{
                name: `${fieldId}_asFactBox`,
                value: isFactBox,
                onChange: () => setFactBox(fieldId, !isFactBox),
              }}
              label={p('textAsFactBox')}
              description={p('textAsFactBoxHint')}
            />
          </NewsFieldWrapper>
        );
      }
      case 'IMAGE_PHOTOGRAPHER':
        return (
          <NewsFieldWrapper {...additionalData}>
            <Field
              name={fieldId}
              component={renderTexField}
              placeholder={p('IMAGE_PHOTOGRAPHER_title')}
            />
          </NewsFieldWrapper>
        );
      case 'YOUTUBE_VIDEO':
        return (
          <NewsFieldWrapper {...additionalData}>
            {/* A pasted link is kept as just the video's id */}
            <Labelled label={p('YOUTUBE_VIDEO_title')} hint={p('youtubeHint')}>
              <Field
                name={fieldId}
                component={renderTexField}
                placeholder="https://www.youtube.com/watch?v=…"
                normalize={youtubeId}
              />
            </Labelled>
            {isYoutubeId(values[fieldId]) && (
              <img
                className="news-block__video-thumb"
                src={`https://img.youtube.com/vi/${values[fieldId]}/mqdefault.jpg`}
                alt=""
              />
            )}
          </NewsFieldWrapper>
        );
      case 'SPORTALITY_VIDEO':
        return (
          <NewsFieldWrapper {...additionalData}>
            <Labelled
              label={p('SPORTALITY_VIDEO_title')}
              hint={`${s('example')}: 244304`}
            >
              <Field
                name={fieldId}
                component={renderTexField}
                placeholder="244304"
              />
            </Labelled>
          </NewsFieldWrapper>
        );
      case 'HEADER':
        return (
          <NewsFieldWrapper {...additionalData}>
            <Field
              name={fieldId}
              component={renderTexField}
              placeholder={p('HEADER_title')}
            />
          </NewsFieldWrapper>
        );
      case 'DELIMITER':
        return (
          <NewsFieldWrapper {...additionalData}>
            <Field
              name={fieldId}
              value="test"
              component={() => null}
              placeholder={p('DELIMITER_title')}
            />
          </NewsFieldWrapper>
        );
      case 'I_FRAME': {
        // An AI page is changed with AI; its HTML is there when asked for
        const showHtml = !aiPage || shownHtml.includes(fieldId);
        // Its mark: the page (for its versions), and whether it's been
        // changed by hand since AI wrote it
        const pageInfo = aiPage ? aiPageInfo(values[fieldId]) : null;
        const heading = aiPage ? blockSummary(fieldId, values).text : '';
        return (
          <NewsFieldWrapper {...additionalData}>
            {aiPage && (
              // The page at a glance: a small phone-sized look at it, then
              // what it is, its heading, and what can be done with it
              <div className="ai-page-card">
                <span
                  className="ai-page-thumb__frame"
                  aria-hidden="true"
                  // Links in the page can't be reached from here
                  inert=""
                >
                  <span className="ai-page-thumb__page">
                    <EmbedPreview html={values[fieldId]} fullWidth />
                  </span>
                </span>
                <div className="ai-page-card__body">
                  <span className="ai-page-card__kicker">
                    <CreationIcon size={14} />
                    {p('AI_PAGE_title')}
                  </span>
                  {heading && (
                    <strong className="ai-page-card__heading">{heading}</strong>
                  )}
                  {pageInfo?.handEdited && (
                    <p className="ai-page-card__note" role="note">
                      {p('aiPageHandEdited')}
                    </p>
                  )}
                  <div className="news-block__ai">
                    {aiUsage && (
                      <Button
                        type="button"
                        size="sm"
                        color="primary"
                        className="mb-0"
                        onClick={() => {
                          setAiEditBlock(fieldId);
                          setAiEditOpen(true);
                        }}
                      >
                        <CreationIcon size={16} /> {p('aiEditButton')}
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      outline
                      color="primary"
                      className="mb-0"
                      onClick={() => setTextEditBlock(fieldId)}
                    >
                      <ViewAgendaIcon size={16} /> {p('aiTextEditButton')}
                    </Button>
                    <div className="news-block__ai-end">
                      {pageInfo && (
                        <SquareIconButton
                          icon={<HistoryIcon />}
                          title={p('aiVersionsTitle')}
                          id={`versions-${fieldId}`}
                          onClick={() => setVersionsBlock(fieldId)}
                        />
                      )}
                      <SquareIconButton
                        icon={<CodeTagsIcon />}
                        title={
                          showHtml ? p('aiPageHideHtml') : p('aiPageShowHtml')
                        }
                        id={`html-toggle-${fieldId}`}
                        active={showHtml}
                        pressed={showHtml}
                        onClick={toggleHtml(fieldId)}
                      />
                      <SquareIconButton
                        icon={
                          copiedHtml === fieldId ? (
                            <CheckIcon />
                          ) : (
                            <ContentCopyIcon />
                          )
                        }
                        title={p('aiPageCopy')}
                        id={`copy-${fieldId}`}
                        onClick={copyHtml(fieldId)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div
              id={`html-${fieldId}`}
              hidden={!showHtml}
              className={aiPage ? 'news-block__code' : undefined}
            >
              <Labelled label={aiPage ? p('aiPageHtml') : p('embedCode')}>
                <Field
                  name={fieldId}
                  component={renderTextAreaField}
                  placeholder="<iframe src=…></iframe>"
                />
              </Labelled>
            </div>
            {/* The app shows it edge to edge, without the side margins */}
            <Field
              name={`${fieldId}_fullWidth`}
              component={SwitchRow}
              label={p('I_FRAME_fullWidth')}
            />
          </NewsFieldWrapper>
        );
      }
      case 'CUSTOM_VIDEO':
        return (
          <NewsFieldWrapper {...additionalData}>
            <Field name={fieldId} component={renderDropZoneVideoField} />
          </NewsFieldWrapper>
        );
      case 'VIDEO':
        return (
          <NewsFieldWrapper {...additionalData}>
            <Labelled label={p('videoSource')}>
              <ChoiceCards
                input={{
                  name: fieldId,
                  value: '',
                  onChange: chooseVideoSource(fieldId),
                }}
                options={videoSources}
                ariaLabel={p('videoSource')}
                columns={videoSources.length === 3 ? 3 : 2}
              />
            </Labelled>
          </NewsFieldWrapper>
        );
      default:
        return null;
    }
  };
  useEffect(() => {
    change('selectedColums', columns);
    if (columns && formValues) {
      newsObjects(columns, formValues);
    }
    // change/formValues/newsObjects are redefined every render (newsObjects
    // is an inline prop from the parent); depending on them would re-run
    // this effect on every render instead of only when columns/components
    // change.
    // eslint-disable-next-line
  }, [columns, components]);
  return (
    <>
      {/* Switching tabs keeps the content, so only leaving the page warns */}
      <Prompt
        when={dirty}
        message={(location) =>
          location.pathname === pathname || p('leaveWarning')
        }
      />
      <AppPreviewModal
        visible={previewModalVisible}
        toggle={() => setPreviewModalVisible(false)}
        components={formValues}
        selectedItems={items}
        news={news}
      />
      <AiDraftModal
        isOpen={aiModalOpen}
        usage={aiUsage}
        onClose={() => setAiModalOpen(false)}
        onInsert={insertAiPage}
        onStarted={loadAiUsage}
      />
      <AiVersionsModal
        isOpen={!!versionsBlock}
        pageId={
          versionsBlock ? aiPageInfo(values[versionsBlock])?.pageId : null
        }
        onClose={() => setVersionsBlock(null)}
        onUse={restoreAiVersion}
      />
      <AiTextEditorModal
        isOpen={!!textEditBlock}
        html={textEditBlock ? values[textEditBlock] : ''}
        onClose={() => setTextEditBlock(null)}
        onSave={saveAiText}
      />
      <AiDraftModal
        isOpen={aiEditOpen}
        usage={aiUsage}
        source={aiEditBlock ? values[aiEditBlock] : null}
        onClose={() => setAiEditOpen(false)}
        onInsert={replaceAiPage}
        onStarted={loadAiUsage}
      />
      <FormModal
        isOpen={emptyModalOpen}
        onClose={() => setEmptyModalOpen(false)}
        title={p('emptyBlocksTitle')}
      >
        <div className="qm-body">
          <p className="mb-0">{p('emptyBlocksText')}</p>
          <ul className="news-empty-list">
            {emptyBlocks.map((block) => {
              const type = block.id.split('-')[0];
              const Icon = getIconComponent(type);
              return (
                <li key={block.id}>
                  <Icon size={16} />
                  {p('blockNumber', { number: block.index + 1 })} ·{' '}
                  {p(`${type}_title`)}
                </li>
              );
            })}
          </ul>
        </div>
        <div className="qm-footer">
          <Button
            type="button"
            outline
            color="secondary"
            className="mb-0"
            onClick={() => {
              setEmptyModalOpen(false);
              save(true);
            }}
          >
            {p('publishAnyway')}
          </Button>
          <Button
            type="button"
            color="primary"
            className="mb-0"
            onClick={fillInEmptyBlocks}
          >
            {p('fillThemIn')}
          </Button>
        </div>
      </FormModal>
      <form className="form news-builder" ref={nodeRef}>
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="news-builder__layout">
            <div className="news-builder__blocks">
              <Droppable droppableId="Col1" key="Col1">
                {(provided, snapshot) => (
                  <div
                    className={`news-builder__list${
                      snapshot.isDraggingOver ? ' news-builder__list--over' : ''
                    }`}
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {items.length === 0 && (
                      <div className="news-builder__empty">
                        <EmptyStateInfo
                          icon="layout"
                          title={s('emptyStateTitle')}
                          text={p('emptyAppComponents')}
                        />
                        <div className="news-builder__empty-actions">
                          <AddBlockMenu aiPage={!!aiUsage} onAdd={addAt(0)} />
                          <Button
                            type="button"
                            outline
                            color="primary"
                            className="mb-0"
                            onClick={() =>
                              addBlocks(['RICH_TEXT2', 'IMAGE'], 0)
                            }
                          >
                            {p('startWithTextAndImage')}
                          </Button>
                        </div>
                      </div>
                    )}
                    {items.map((item, idx) => (
                      <Draggable
                        key={item.id}
                        draggableId={`${item.id}Col1`}
                        index={idx}
                      >
                        {(provided2) => (
                          <div
                            ref={provided2.innerRef}
                            {...provided2.draggableProps}
                          >
                            <AddBlockMenu
                              inline
                              aiPage={!!aiUsage}
                              onAdd={addAt(idx)}
                            />
                            {renderField(
                              item.id,
                              idx,
                              items.length,
                              provided2.dragHandleProps
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
              {items.length > 0 && (
                <div className="news-builder__add-end">
                  <AddBlockMenu
                    aiPage={!!aiUsage}
                    onAdd={addAt(items.length)}
                  />
                </div>
              )}
            </div>
            <div className="news-builder__palette">
              <Droppable droppableId="Col0" key="Col0">
                {(provided) => (
                  <div
                    className="comCol news-builder__palette-list"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {palette.map((item, index) => (
                      <Draggable
                        key={item.id}
                        draggableId={`${item.id}Col0`}
                        index={index}
                      >
                        {(provided2, snapshot2) => (
                          <div
                            ref={provided2.innerRef}
                            {...provided2.draggableProps}
                            {...provided2.dragHandleProps}
                          >
                            <ContentBuilderCard
                              item={item}
                              isDragging={snapshot2.isDragging}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </div>
        </DragDropContext>
        <div className="wl-savebar news-builder__bar">
          <span className="wl-savebar__status">
            {dirty && <span className="wl-unsaved">{p('unsavedChanges')}</span>}
            {!dirty && saved && (
              <span className="wl-saved">
                <CheckIcon size={16} />
                {p('allSaved')}
              </span>
            )}
          </span>
          <span className="wl-savebar__actions">
            <Link className="btn btn-outline-secondary btn-sm mb-0" to="/news">
              {s('close')}
            </Link>
            <Button
              type="button"
              outline
              color="primary"
              size="sm"
              className="mb-0"
              disabled={saving}
              onClick={() => save(false)}
            >
              {s('save')}
            </Button>
            <Button
              type="button"
              color="primary"
              size="sm"
              className="mb-0"
              disabled={saving}
              onClick={handlePublish}
            >
              {t('NewsFormPage.publish')}
            </Button>
          </span>
        </div>
      </form>
    </>
  );
};
const reduxF = reduxForm({
  form: 'news_components_form',
});

export default connect((state) => ({
  formValues: getFormValues('news_components_form')(state),
}))(reduxF(NewsComponentsForm));
